import {
  Component,
  ViewChild,
  OnInit,
  OnDestroy,
  effect,
  inject,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { PopoverModule, Popover } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { SelectModule } from 'primeng/select';
import { DynamicDialogModule } from 'primeng/dynamicdialog';
import { DialogService } from 'primeng/dynamicdialog';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { PreferencesService, AuthService } from '../../core/services';
import { User } from '../../models/user';
import { BalanceStore } from '../../core/stores';
import { AVAILABLE_LANGUAGES, DEFAULT_LANGUAGE, Language } from '../../core/constants';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  HelpCenterComponent,
  BalanceHistoryComponent,
} from '../../features/dialogs';
import { UserPopoverComponent } from '../../features/others/user-popover/user-popover.component';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-mobile-header',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    PopoverModule,
    ButtonModule,
    AvatarModule,
    SelectModule,
    DynamicDialogModule,
    TranslocoPipe,
    UserPopoverComponent,
  ],
  templateUrl: './mobile-header.component.html',
  styleUrls: ['./mobile-header.component.scss'],
  providers: [DialogService],
})
export class MobileHeaderComponent implements OnInit, OnDestroy {
  @ViewChild('userPopover') userPopover!: Popover;

  isDark = false;
  currentUser: User | null = null;
  balanceStore = inject(BalanceStore);
  preferencesService = inject(PreferencesService);
  authService = inject(AuthService);
  translocoService = inject(TranslocoService);
  dialogService = inject(DialogService);
  router = inject(Router);
  cdr = inject(ChangeDetectorRef);

  private scrollSubscription?: Subscription;

  availableLanguages = AVAILABLE_LANGUAGES;
  currentLanguage: Language =
    AVAILABLE_LANGUAGES.find((lang) => lang.code === DEFAULT_LANGUAGE) || AVAILABLE_LANGUAGES[0];

  // App version info
  appVersion = environment.version;
  appName = environment.appName;

  constructor() {
    effect(() => {
      this.currentUser = this.authService.currentUser?.user || null;
    });
  }

  get currentUserBalance(): string {
    return this.balanceStore.balance.formatted();
  }

  get balanceLoading(): boolean {
    return this.balanceStore.balance.loading();
  }

  ngOnInit() {
    this.checkTheme();
    this.initializeLanguage();
  }

  ngOnDestroy() {
    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
    }
  }

  private checkTheme() {
    this.isDark = this.preferencesService.darkMode;
    if (this.isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  toggleTheme() {
    this.preferencesService.toggleDarkMode();
    this.isDark = this.preferencesService.darkMode;
    if (this.isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  toggleUserPopover(event: any) {
    this.userPopover.toggle(event);
  }

  onLogoutClick() {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  goToProfile() {
    this.userPopover.hide();
    this.router.navigate(['/profile']);
  }

  goToBalanceHistory() {
    this.dialogService.open(BalanceHistoryComponent, {
      header: this.translocoService.translate('header.user.balanceHistory'),
      width: '100vw',
      styleClass: 'balance-history-dialog',
      modal: true,
      maximizable: true,
      dismissableMask: true,
      closable: true,
    });
    this.userPopover.hide();
  }

  goToHelpCenter() {
    this.dialogService.open(HelpCenterComponent, {
      header: this.translocoService.translate('header.user.helpCenter'),
      width: '60vw',
      styleClass: 'help-center-dialog',
      modal: true,
      dismissableMask: true,
      closable: true,
    });
    this.userPopover.hide();
  }

  goToSettings() {
    this.userPopover.hide();
    this.router.navigate(['/settings']);
  }

  logout() {
    this.authService.logout();
    this.userPopover.hide();
    this.router.navigate(['/login']);
  }


  private initializeLanguage() {
    const savedLanguage = localStorage.getItem('selectedLanguage') || DEFAULT_LANGUAGE;
    const language = this.availableLanguages.find((lang) => lang.code === savedLanguage);
    if (language) {
      this.currentLanguage = language;
      this.translocoService.setActiveLang(language.code);
    }
  }

  onLanguageSelect(language: Language) {
    this.currentLanguage = language;
    this.translocoService.setActiveLang(language.code);
    localStorage.setItem('selectedLanguage', language.code);

    const direction =
      this.currentLanguage.code === 'fa' || this.currentLanguage.code === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.dir = direction;
    document.documentElement.lang = this.currentLanguage.code;
  }

  selectLanguage(language: Language) {
    this.userPopover.hide();
    this.currentLanguage = language;
    this.translocoService.setActiveLang(language.code);
    localStorage.setItem('selectedLanguage', language.code);

    const direction =
      this.currentLanguage.code === 'fa' || this.currentLanguage.code === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.dir = direction;
    document.documentElement.lang = this.currentLanguage.code;
  }
}
